import React from "react";
import { ClipOverlay, ZoomConfig } from "../../../types";
import { MediaPaddingControls } from "../common/media-padding-controls";
import { MediaFilterPresetSelector } from "../common/media-filter-preset-selector";
import { ZoomKeyframePanel } from "./zoom-keyframe-panel";
import { Slider } from "../../ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

/**
 * Props for the VideoStylePanel component
 * @interface VideoStylePanelProps
 * @property {ClipOverlay} localOverlay - The current overlay object containing video styles
 * @property {Function} handleStyleChange - Callback function to update overlay styles
 * @property {number} currentFrame - Current playback frame position (optional)
 * @property {Function} onZoomConfigChange - Callback function to update zoom configuration (optional)
 */
interface VideoStylePanelProps {
  localOverlay: ClipOverlay;
  handleStyleChange: (updates: Partial<ClipOverlay["styles"]>) => void;
  currentFrame?: number;
  onZoomConfigChange?: (zoomConfig: ZoomConfig) => void;
}

/**
 * VideoStylePanel Component
 *
 * A panel that provides controls for styling video overlays. It allows users to adjust:
 * - Object fit (cover, contain, fill)
 * - Border radius
 * - Filter presets (retro, vintage, Wes Anderson, etc.)
 * - Brightness, Contrast, Saturation adjustments
 * - Padding and padding background color
 *
 * The component uses a local overlay state and propagates changes through the handleStyleChange callback.
 * All style controls maintain both light and dark theme compatibility.
 *
 * @component
 * @param {VideoStylePanelProps} props - Component props
 * @returns {JSX.Element} A styled form containing video appearance controls
 */
export const VideoStylePanel: React.FC<VideoStylePanelProps> = ({
  localOverlay,
  handleStyleChange,
  currentFrame = 0,
  onZoomConfigChange,
}) => {
  // Helper to extract filter value from CSS filter string
  const getFilterValue = (filterName: string, defaultValue: number = 100): number => {
    const regex = new RegExp(`${filterName}\\((\\d+)%\\)`);
    const match = localOverlay?.styles?.filter?.match(regex);
    return match ? parseInt(match[1]) : defaultValue;
  };

  // Helper to update a specific filter value
  const updateFilter = (filterName: string, value: number) => {
    const currentFilter = localOverlay?.styles?.filter || "";
    // Remove existing filter of this type
    const regex = new RegExp(`${filterName}\\(\\d+%\\)`, "g");
    const cleanedFilter = currentFilter.replace(regex, "").trim();
    // Add new filter
    const newFilter = cleanedFilter
      ? `${cleanedFilter} ${filterName}(${value}%)`
      : `${filterName}(${value}%)`;
    handleStyleChange({ filter: newFilter.trim() });
  };

  return (
    <div className="space-y-2">
      {/* Appearance Settings */}
      <div className="space-y-4 rounded-md bg-card p-4 border">
        <h3 className="text-sm font-extralight text-foreground">Appearance</h3>

        <div className="space-y-2">
          <label className="text-xs font-extralight">
            Fit
          </label>
          <Select
            value={localOverlay?.styles?.objectFit ?? "cover"}
            onValueChange={(value) =>
              handleStyleChange({ objectFit: value as any })
            }
          >
            <SelectTrigger className="w-full font-extralight shadow-none">
              <SelectValue placeholder="Select fit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cover">Cover</SelectItem>
              <SelectItem value="contain">Contain</SelectItem>
              <SelectItem value="fill">Fill</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Border Radius */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-extralight">
              Border Radius
            </label>
            <span className="text-xs min-w-[40px] text-right">
              {localOverlay?.styles?.borderRadius ?? "0px"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Slider
              value={[
                parseInt(localOverlay?.styles?.borderRadius ?? "0")
              ]}
              onValueChange={(value) =>
                handleStyleChange({ borderRadius: `${value[0]}px` })
              }
              min={0}
              max={50}
              step={1}
              className="flex-1"
            />
          </div>
        </div>

        {/* Media Padding Controls */}
        <MediaPaddingControls
          localOverlay={localOverlay}
          handleStyleChange={handleStyleChange}
        />
      </div>

      {/* Filter Presets */}
      <div className="space-y-4 rounded-md bg-card p-4 border">
        <h3 className="text-sm font-extralight text-foreground">Filter Presets</h3>
        <MediaFilterPresetSelector
          localOverlay={localOverlay}
          handleStyleChange={handleStyleChange}
        />
      </div>

      {/* Filter Adjustments */}
      <div className="space-y-4 rounded-md bg-card p-4 border">
        <h3 className="text-sm font-extralight text-foreground">Adjustments</h3>

        {/* Brightness */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-extralight">Brightness</label>
            <span className="text-xs min-w-[40px] text-right">
              {getFilterValue("brightness", 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Slider
              value={[getFilterValue("brightness", 100)]}
              onValueChange={(value) => updateFilter("brightness", value[0])}
              min={0}
              max={200}
              step={1}
              className="flex-1"
            />
          </div>
        </div>

        {/* Contrast */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-extralight">Contrast</label>
            <span className="text-xs min-w-[40px] text-right">
              {getFilterValue("contrast", 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Slider
              value={[getFilterValue("contrast", 100)]}
              onValueChange={(value) => updateFilter("contrast", value[0])}
              min={0}
              max={200}
              step={1}
              className="flex-1"
            />
          </div>
        </div>

        {/* Saturation */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-extralight">Saturation</label>
            <span className="text-xs min-w-[40px] text-right">
              {getFilterValue("saturate", 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Slider
              value={[getFilterValue("saturate", 100)]}
              onValueChange={(value) => updateFilter("saturate", value[0])}
              min={0}
              max={200}
              step={1}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Zoom Effects */}
      {onZoomConfigChange && (
        <div className="space-y-4 rounded-md bg-card p-4 border">
          <h3 className="text-sm font-extralight text-foreground">Zoom Effects</h3>
          <ZoomKeyframePanel
            localOverlay={localOverlay}
            currentFrame={currentFrame}
            onZoomConfigChange={onZoomConfigChange}
          />
        </div>
      )}
    </div>
  );
};
